import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { IconButton, Typography } from '@material-ui/core';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import { useInicioApps } from './InicioAppsContext';
import {
  imagenDesdeItem,
  INICIO_APPS_DESKTOP_MIN_PX,
  itemEsAccesoLogoAncho,
  redireccionarAccesoRapido,
} from './inicioAppsShared';
import '../../global/css/inicioSidebarApps.css';

export { GENERICA_SIDEBAR_APPS } from './inicioAppsShared';

const SELECTOR_ZONA_HOLA =
  '.contenedor_menu.inicio-contenedor.inicio-principal .inicio-zona-hola';
const SELECTOR_BLOQUE_FINANCIERO =
  '.contenedor_menu.inicio-contenedor.inicio-principal .inicio-section-financiero.inicio-section-card';

function InicioSidebarApps() {
  const { items, cargado } = useInicioApps();
  const isDesktop = useMediaQuery(
    `(min-width:${INICIO_APPS_DESKTOP_MIN_PX}px)`,
    { defaultMatches: true }
  );

  const [oculto, setOculto] = useState(false);
  const [anchorTop, setAnchorTop] = useState(null);
  const [panelMaxHeight, setPanelMaxHeight] = useState(null);

  const visibles = useMemo(() => items, [items]);

  const updateAnchor = useCallback(() => {
    const zonaHola = document.querySelector(SELECTOR_ZONA_HOLA);
    const bloqueFinanciero = document.querySelector(SELECTOR_BLOQUE_FINANCIERO);
    if (!zonaHola) return;

    const holaRect = zonaHola.getBoundingClientRect();
    const top = Math.max(74, Math.round(holaRect.top));
    setAnchorTop(top);

    if (bloqueFinanciero) {
      const financieroRect = bloqueFinanciero.getBoundingClientRect();
      const alto = Math.max(180, Math.round(financieroRect.bottom - holaRect.top));
      setPanelMaxHeight(alto);
      return;
    }

    setPanelMaxHeight(null);
  }, []);

  useLayoutEffect(() => {
    if (!cargado || !isDesktop) return undefined;
    updateAnchor();
    const t0 = window.setTimeout(updateAnchor, 0);
    const t1 = window.setTimeout(updateAnchor, 80);
    const t2 = window.setTimeout(updateAnchor, 320);
    window.addEventListener('resize', updateAnchor);
    const scrollEl = document.querySelector(
      '#seccion-vista .contenedor_menu.inicio-contenedor'
    );
    if (scrollEl) {
      scrollEl.addEventListener('scroll', updateAnchor, { passive: true });
    }
    return () => {
      clearTimeout(t0);
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener('resize', updateAnchor);
      if (scrollEl) {
        scrollEl.removeEventListener('scroll', updateAnchor);
      }
    };
  }, [cargado, isDesktop, visibles.length, updateAnchor]);

  if (!cargado || !isDesktop || !visibles.length) return null;

  const panel = (
    <>
      <div
        className="inicio-sidebar-apps-root"
        aria-label="Accesos rápidos"
        style={
          anchorTop != null
            ? { top: anchorTop, transform: 'none' }
            : undefined
        }
      >
        {oculto ? (
          <IconButton
            className="inicio-sidebar-apps-peek"
            onClick={() => setOculto(false)}
            aria-label="Mostrar accesos rápidos"
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        ) : (
          <div className="inicio-sidebar-apps-host">
            <div
              className="inicio-sidebar-apps-panel"
              style={panelMaxHeight ? { maxHeight: panelMaxHeight } : undefined}
            >
              <IconButton
                className="inicio-sidebar-apps-toggle"
                size="small"
                onClick={() => setOculto(true)}
                aria-label="Ocultar accesos rápidos"
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
              <div className="inicio-sidebar-apps-list">
                {visibles.map((item) => {
                  const src = imagenDesdeItem(item);
                  const compact = itemEsAccesoLogoAncho(item);
                  return (
                    <IconButton
                      key={item.id ?? `${item.nombre}-${item.valorb}`}
                      className={[
                        'inicio-sidebar-apps-icon-btn',
                        compact ? 'inicio-sidebar-apps-icon-btn--compact' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => redireccionarAccesoRapido(item)}
                      title={item.nombre || 'App'}
                    >
                      {src ? (
                        <img src={src} alt="" />
                      ) : (
                        <Typography variant="caption" style={{ fontSize: 10 }}>
                          {(item.nombre || '?').slice(0, 2)}
                        </Typography>
                      )}
                    </IconButton>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );

  if (typeof document === 'undefined') return panel;
  return createPortal(panel, document.body);
}

export default InicioSidebarApps;
